import { createContext, CSSProperties, ReactNode, useCallback, useContext } from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** Drag state for one SortableList item, readable by any descendant of that item. */
export interface SortableItemState {
  readonly dragHandleProps: SortableDragHandleProps;
  readonly isDragging: boolean;
  readonly isDisabled: boolean;
}

const SortableItemContext = createContext<SortableItemState | undefined>(undefined);

/**
 * Returns the enclosing SortableList item's drag state; use to wire a drag handle that lives in a
 * child component. The nearest item wins, so handles inside nested lists bind to their own list.
 */
export function useSortableItem(): SortableItemState {
  const state = useContext(SortableItemContext);
  if (!state) {
    throw new Error('useSortableItem must be used within a SortableList item');
  }
  return state;
}

interface SortableItemProps<T> {
  readonly id: string;
  readonly isDisabled: boolean;
  readonly value: T;
  readonly item: (value: T, args: SortableRenderArgs) => ReactNode;
}

function SortableItem<T>({ id, isDisabled, value, item }: SortableItemProps<T>) {
  // Bind this item to dnd-kit's sortable context.
  const {
    setNodeRef,
    transform,
    transition,
    attributes,
    listeners,
    setActivatorNodeRef,
    isDragging,
  } = useSortable({ id, disabled: isDisabled });

  // Spread onto the outer element to track the cursor while dragging
  // and animate into place after a drop.
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Bundle the drag-handle wiring so the renderer can attach it to
  // its handle element without depending on dnd-kit directly.
  const dragHandleProps: SortableDragHandleProps = {
    attributes: attributes as unknown as Record<string, unknown>,
    listeners: listeners as unknown as Record<string, (event: unknown) => void> | undefined,
    setActivatorNodeRef,
  };

  // Expose the drag state to descendants so handles can live in child components.
  const itemState: SortableItemState = { dragHandleProps, isDragging, isDisabled };
  return (
    <SortableItemContext.Provider value={itemState}>
      {item(value, { setNodeRef, style, dragHandleProps, isDragging })}
    </SortableItemContext.Provider>
  );
}

/**
 * Drag-handle props the renderer must spread onto the handle element
 * (typically an icon button) for keyboard/pointer activation.
 */
export interface SortableDragHandleProps {
  readonly attributes: Record<string, unknown>;
  readonly listeners: Record<string, (event: unknown) => void> | undefined;
  readonly setActivatorNodeRef: (node: HTMLElement | null) => void;
}

/** Render-prop arguments for one item in a SortableList. */
export interface SortableRenderArgs {
  /** Ref the renderer must place on the outer element of the row/card. */
  readonly setNodeRef: (node: HTMLElement | null) => void;
  /** Style the renderer must spread onto the outer element (transform + transition). */
  readonly style: CSSProperties;
  /** Props the renderer must spread onto the drag-handle element. */
  readonly dragHandleProps: SortableDragHandleProps;
  /** True while this item is being dragged. */
  readonly isDragging: boolean;
}

export interface SortableListProps<T> {
  /** Stable ordered list of items. */
  readonly items: readonly T[];
  /** Returns a stable id for each item; used as DnD key + React key. */
  readonly keyExtractor: (value: T) => string;
  /**
   * Called with the new ordered ids after a drop. Not called when the
   * drop position equals the start position (no-op short-circuit).
   */
  readonly onReorder: (newOrderIds: readonly string[]) => void;
  /** Optional id used to scope the SortableContext (e.g. `epic-${epicId}` for stories). */
  readonly id?: string;
  /** Per-item renderer. */
  readonly item: (value: T, args: SortableRenderArgs) => ReactNode;
  /**
   * Wraps the sortable items (but not DndContext). Use when the items
   * must live inside a specific DOM element (e.g. `<tbody>`) that
   * cannot contain the extra `<div>` DndContext renders.
   */
  readonly container?: (children: ReactNode) => ReactNode;
  /** Disables dragging for every item, e.g. in read-only views. Defaults to false. */
  readonly isDisabled?: boolean;
}

/**
 * Computes the new ordered ids after dropping `activeId` at the slot
 * currently occupied by `overId`. Returns `undefined` when the drop is
 * a no-op (same id, missing id, or no positional change).
 */
export function computeReorderedIds(
  orderedIds: readonly string[],
  activeId: string,
  overId: string | undefined,
): readonly string[] | undefined {
  // Reject obvious no-ops before doing index math.
  if (!overId || activeId === overId) return undefined;

  // Look up positions; either id missing from the list is a no-op.
  const oldIndex = orderedIds.indexOf(activeId);
  const newIndex = orderedIds.indexOf(overId);
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return undefined;

  // Move the active id from its old slot to the new slot.
  const next = orderedIds.slice();
  const [moved] = next.splice(oldIndex, 1);
  // @ts-expect-error TS2345 - strict migration
  next.splice(newIndex, 0, moved);
  return next;
}

/**
 * Renders a vertically reorderable list backed by `@dnd-kit/sortable`.
 * Use to compose sortable rows or cards without leaking dnd-kit
 * primitives into every view. Reorders that don't change the order
 * are short-circuited (no `onReorder` call).
 */
export function SortableList<T>({
  items,
  keyExtractor,
  onReorder,
  id,
  item,
  container,
  isDisabled = false,
}: SortableListProps<T>) {
  // Pointer for mouse/touch drag, Keyboard for accessibility (Tab+Space+arrows).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Recompute every render rather than memoizing on the array reference: legacy callers mutate
  // their items in place, and a stale id list would make reorders drop newly added items.
  // SortableContext compares ids by value, so a fresh array each render is cheap.
  const orderedIds = items.map(keyExtractor);

  // Translate dnd-kit's drop event into a plain id-array reorder.
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const next = computeReorderedIds(
        orderedIds,
        String(event.active.id),
        event.over ? String(event.over.id) : undefined,
      );
      if (next) onReorder(next);
    },
    [orderedIds, onReorder],
  );

  const renderedItems = items.map((value) => (
    <SortableItem
      key={keyExtractor(value)}
      id={keyExtractor(value)}
      isDisabled={isDisabled}
      value={value}
      item={item}
    />
  ));

  return (
    <DndContext
      id={id}
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
        {container ? container(renderedItems) : renderedItems}
      </SortableContext>
    </DndContext>
  );
}
